import { Button, Stack, Typography } from '@mui/material';
import { FC, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { LS_SW } from '../../serviceWorker/appInit';
import { Popup, TPopupComponent } from '../components/popup/popup.abstract';
import { TPopupDefaultActions } from '../components/popup/services/popup.service';
import { snackbarService } from '../components/snackbar/service/snackbar.service';
import { storageHelper } from '../helpers/storage';
import { appState } from '../services/app/app.state';
import { navigateService } from '../services/navigate/navigate.service';

export const useGlobalState = () => {
  const { t } = useTranslation();

  useEffect(() => {
    const offlineHandler = () => appState.pub.networkStatus('offline');
    const onlineHandler = () => appState.pub.networkStatus('online');
    window.addEventListener('offline', offlineHandler);
    window.addEventListener('online', onlineHandler);

    // When service worker is updated and page reload, show snackbar and navigate to the page
    const sw = storageHelper.local.getItem('SW') as LS_SW;
    if (sw?.updated) {
      storageHelper.local.setItem('SW', { updated: false, navigateTo: '' } as LS_SW);
      if (sw.navigateTo) {
        navigateService.pub.navigateTo(sw.navigateTo);
      }
      appState.pub.appUpdate();
    }

    const subAppState = appState.sub.state().subscribe(({ action, networkStatus }) => {
      switch (action) {
        case 'networkStatus': {
          snackbarService.pub.hide(networkStatus === 'offline' ? 'online' : 'offline');
          snackbarService.pub.show({
            id: networkStatus,
            type: networkStatus === 'offline' ? 'warning' : 'success',
            content:
              networkStatus === 'offline' ? t('common.offline') : t('common.internet_restored'),
            noAutoHide: networkStatus === 'offline',
          });
          break;
        }
        case 'logOutFromOtherTab': {
          loggedOutPopup.stream.emit('open');
          break;
        }
        case 'newAppVersionAvailable': {
          snackbarService.pub.show({
            content: <UpdateMessage />,
            type: 'info',
            autoHideDuration: 20000,
          });
        }
      }
    });

    return () => {
      subAppState.unsubscribe();
    };
  }, []);
};

const UpdateMessage: FC = () => {
  const { t } = useTranslation();

  return (
    <>
      <span>{t('common.new_version_available')}.</span>
      <br />
    </>
  );
};

class LoggedOutPopup extends Popup<{ action: TPopupDefaultActions }> {
  Component: FC<TPopupComponent>;

  constructor() {
    super();
    const stream = this.innerStream;

    this.Component = () => {
      const Wrapper = useMemo(() => this.Wrapper, []);
      const { t } = useTranslation();

      return (
        <Wrapper
          stream={stream}
          popupTitle={t('common.session_expired')}
          closeBtn={false}
          closureOverlay={false}
        >
          <Typography>{t('common.signed_out_refresh')}</Typography>
          <Stack justifyContent='center' alignItems='center' mt={2}>
            <Button variant='contained' onClick={() => window.location.reload()}>
              {t('common.reload_page')}
            </Button>
          </Stack>
        </Wrapper>
      );
    };
  }
}

export const loggedOutPopup = new LoggedOutPopup();
